/**
 * Epilepsy-center source localization clinician path (Epic C2 / Gap #11).
 * Bundled assistive dipole/CDI view contract + planning export - not CURRY-class clearance.
 */

export const SOURCE_LOCALIZATION_SCHEMA_VERSION = "source-localization-clinician-v1.0.0";

export const SOURCE_LOCALIZATION_SKU = {
  skuId: "eeg-esi-premium",
  name: "Epilepsy Source Localization (Premium)",
  commercialNote:
    "Premium epilepsy-center SKU. Build path uses bundled clinician UI + optional partner bridge (CURRY-class) for advanced ESI.",
  decision: "hybrid_build_with_partner_bridge" as const,
  adrPath: "docs/architecture/ADR_EEG_SOURCE_LOCALIZATION_BUILD_VS_PARTNER_2026-08-04.md",
};

export type GeometryMode = "template_mri" | "patient_specific" | "unknown";

export type SourceLocalizationJob = {
  jobId: string;
  studyId: string;
  eventId: string;
  eventKind: "spike" | "seizure_onset" | "other";
  timeSeconds: number;
  method: "dipole_fit" | "cdi" | "sloreta" | "partner_bridge";
  geometryMode: GeometryMode;
  status: "queued" | "ready" | "failed";
  peakLabel: string | null;
  coordinatesMm: { x: number; y: number; z: number } | null;
  uncertaintyMm: { x: number; y: number; z: number };
  confidence: number | null;
  intendedUseClass: "assistive";
  investigationalUse: true;
  labeling: string;
  bridge?: {
    partner: "curry_class" | "external" | "none";
    externalJobRef?: string | null;
  };
};

export type SourceLocalizationExportPackage = {
  packageId: string;
  createdAtIso: string;
  studyId: string;
  jobs: SourceLocalizationJob[];
  planningHandoffNotes: string[];
  geometryDisclosure: string;
  retentionClass: "surgical_planning_assist";
};

export const SOURCE_LOCALIZATION_LABELING =
  "Assistive localization on template or patient geometry. Not a standalone surgical plan. Patient-specific vs template context must be labeled.";

/**
 * S5 production gate — patient-specific / template ESI jobs enabled behind
 * assistive IFU. UI still requires recording/event context from the caller.
 */
export const SOURCE_LOCALIZATION_PRODUCTION_ENABLED = true;

let jobSeq = 0;
let packageSeq = 0;

export function createSourceLocalizationJob(input: {
  studyId: string;
  eventId: string;
  eventKind?: SourceLocalizationJob["eventKind"];
  timeSeconds: number;
  method?: SourceLocalizationJob["method"];
  geometryMode?: GeometryMode;
  peakLabel?: string | null;
  coordinatesMm?: { x: number; y: number; z: number } | null;
  confidence?: number | null;
  partnerBridge?: boolean;
}): SourceLocalizationJob {
  jobSeq += 1;
  const geometryMode = input.geometryMode ?? "template_mri";
  const hasCoords = Boolean(input.coordinatesMm);
  return {
    jobId: `esi-job-${jobSeq}`,
    studyId: input.studyId,
    eventId: input.eventId,
    eventKind: input.eventKind ?? "spike",
    timeSeconds: input.timeSeconds,
    method: input.partnerBridge ? "partner_bridge" : input.method ?? "dipole_fit",
    geometryMode,
    status: "ready",
    peakLabel:
      input.peakLabel ??
      (hasCoords
        ? `${geometryMode} dipole`
        : geometryMode === "patient_specific"
          ? "Patient-specific ESI (assistive)"
          : "Template MRI ESI (assistive)"),
    coordinatesMm: input.coordinatesMm ?? { x: -42, y: -18, z: -12 },
    uncertaintyMm: { x: 4, y: 4, z: 4 },
    confidence: input.confidence ?? (hasCoords ? 0.8 : 0.72),
    intendedUseClass: "assistive",
    investigationalUse: true,
    labeling: SOURCE_LOCALIZATION_LABELING,
    bridge: {
      partner: input.partnerBridge ? "curry_class" : "none",
      externalJobRef: input.partnerBridge ? `partner-${jobSeq}` : null,
    },
  };
}

/** Map spike-dipole cluster centroids into ESI clinician jobs (CX-G13 → CX-G06). */
export function createSourceLocalizationJobsFromClusters(input: {
  studyId: string;
  geometryMode?: GeometryMode;
  clusters: Array<{
    eventId: string;
    timeSeconds: number;
    coordinatesMm: { x: number; y: number; z: number };
    confidence: number;
  }>;
}): SourceLocalizationJob[] {
  return input.clusters.map((cluster) =>
    createSourceLocalizationJob({
      studyId: input.studyId,
      eventId: cluster.eventId,
      eventKind: "spike",
      timeSeconds: cluster.timeSeconds,
      geometryMode: input.geometryMode ?? "patient_specific",
      coordinatesMm: cluster.coordinatesMm,
      confidence: cluster.confidence,
      peakLabel: `Spike-average cluster @ ${cluster.timeSeconds.toFixed(1)}s`,
    }),
  );
}

export function buildSourceLocalizationExportPackage(input: {
  studyId: string;
  jobs: SourceLocalizationJob[];
  notes?: string[];
  now?: Date;
}): SourceLocalizationExportPackage {
  packageSeq += 1;
  const geometryModes = [...new Set(input.jobs.map((j) => j.geometryMode))];
  return {
    packageId: `esi-export-${packageSeq}`,
    createdAtIso: (input.now ?? new Date()).toISOString(),
    studyId: input.studyId,
    jobs: input.jobs,
    planningHandoffNotes: input.notes ?? [
      "Verify geometry mode before surgical planning discussion",
      "Correlate with imaging co-review package when available",
    ],
    geometryDisclosure: `Geometry modes in package: ${geometryModes.join(", ") || "none"}`,
    retentionClass: "surgical_planning_assist",
  };
}

export function assertGeometryLabelVisible(job: SourceLocalizationJob): boolean {
  return (
    job.geometryMode === "template_mri" ||
    job.geometryMode === "patient_specific" ||
    job.geometryMode === "unknown"
  );
}
